"use client"

import type { Artwork } from "#models/artwork"
import type { ShoppingCart } from "#models/shopping/shoppingCart"

import { useTransition } from "react"
import { useRouter } from "next/navigation"
import toast from "react-hot-toast"

import { LoadingSpinner } from "#components/loadingSpinner/loadingSpinner"
import { CONTACT_EMAIL } from "#environment/constants"
import { useShoppingContext } from "#modules/cart/context/cart.context"
import { cartServiceClient } from "#modules/cart/services/cart.client.service"
import { addCartItem } from "#modules/cart/services/cart.serverAction"
import { useTranslation } from "#modules/localization/translations.client"

export function PurchaseButton({ artwork, canBuyItem, shoppingCart }: { artwork: Artwork; canBuyItem: boolean; shoppingCart?: ShoppingCart }) {
  const { t } = useTranslation("page/artwork")

  const router = useRouter()

  const [_, startTransition] = useTransition()
  const { setCartPopupOpen, optimisticCart, setOptimisticCart } = useShoppingContext()

  // TODO logik overhaulen und besser lesbar machen
  const optCanBuyItem = !artwork.sold ? (optimisticCart?.type === "optimistic" ? !cartServiceClient.hasItem(optimisticCart, artwork.id) : canBuyItem) : false
  const buttonText = !artwork.sold ? (optCanBuyItem ? t("buySection.purchaseButton_notInCart") : t("buySection.purchaseButton_alreadyInCart")) : t("buySection.purchaseButton_sold")

  return (
    <button
      onClick={async () => {
        if (fbq)
          fbq("track", "AddToCart", { content_name: artwork.name, contents: [{ id: artwork.id, quantity: 1 }], content_type: "product", value: artwork.price, currency: "EUR" })

        const cart = optimisticCart ?? shoppingCart!
        const calcShoppingCart = cartServiceClient.optimisticAddItem(cart, artwork)

        // startTransition(() => {
        setOptimisticCart(calcShoppingCart)
        // })

        if (window.matchMedia("(min-width: 640px)").matches) {
          setCartPopupOpen(true)
        }

        const currentPage = window.location.href

        try {
          const shoppingCartNew = await addCartItem(artwork.id)
          // TODO DEV-157 Overhaul Cart Optimistic Updates - Using this approach because useOptimistic doesnt play well with suspense in next14
          setOptimisticCart(shoppingCartNew)
        } catch (e) {
          setCartPopupOpen(false)
          setOptimisticCart(null)
          toast.error(t("buySection.couldNotAddToCart", { CONTACT_EMAIL }), { position: "top-right", style: { backgroundColor: "#ef4444", color: "white" } })
          return
        } finally {
          // TODO DEV-157 Overhaul Cart Optimistic Updates - Using this approach because useOptimistic doesnt play well with suspense in next14
          // Workaround to refresh page when user has navigated while action is in progress
          if (window.location.href !== currentPage) router.refresh()
        }
      }}
      disabled={!optCanBuyItem}
      className="mb-8 mt-7 flex w-full items-center justify-center gap-x-1.5 bg-gold-100 px-6 py-4 font-inter text-nm font-bold text-black transition-all hover:bg-purple-100 hover:text-gold-100 disabled:bg-gray-30 disabled:text-gray-70 xs:gap-x-3 sm:mt-9 xl:text-bg 2xl:py-5 2xl:text-sub">
      {optimisticCart?.type === "optimistic" && !optCanBuyItem && <LoadingSpinner className={"mb-0.5 h-6 w-6 text-gray-70 [--thickness:2.3px]"} />}
      {buttonText}
    </button>
  )
}
